import { Edit } from '@mui/icons-material'
import {
  Box,
  Divider,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'

import KanseiBreadcrumbs from 'components/breadcrumb'
import KanseiButton from 'components/button'
import KanseiDialog from 'components/dialog'
import KanseiMultiSelectBox from 'components/multiSelect'
import KanseiSimpleSelectBox from 'components/simpleSelectBox'
import KanseiTextField from 'components/textField'
import { authApi } from 'hooks/api/auth'
import { companyApi } from 'hooks/api/company'
import { Company } from 'types/company'
import {
  CompanyBranch,
  CompanyBranchRolePermission,
  CompanyBranchRolePermissionBits,
  CONTRACT_TYPE,
  CONTRACT_TYPE_OPTIONS,
  ContractType,
  newCompanyBranchRolePermissions,
  PLUGIN_OPTIONS,
} from 'types/companyBranch'
import { Option } from 'types/option'
import { REGION_OPTIONS } from 'types/region'
import { User } from 'types/user'

import SecurityComponent from './components/security'

export interface BranchForm {
  branchName: string
  postalCode: string
  prefecture: number
  address: string
  phoneNumber: string
  faxNumber: string
  note: string
  plugins: number[]
  contractType: ContractType
}

export interface UserForm {
  id?: string
  loginId: string
  name: string
  email: string
  roleIds?: string[]
  status: number
  /** アクセス可能な支店ID一覧 */
  branchIds?: string[]
}

export interface RoleForm {
  id?: string
  name: string
  permissions: number[]
}

const STATUS = {
  ACTIVE: 0,
  DELETED: 1,
}

const STATUS_OPTIONS: Option[] = [
  {
    label: 'アクティブ',
    value: STATUS.ACTIVE,
  },
  {
    label: '削除',
    value: STATUS.DELETED,
  },
]

export default function CompanyBranchPage() {
  const [companyBranch, setCompanyBranch] = useState<CompanyBranch>()
  const [company, setCompany] = useState<Company>()
  const [open, setOpen] = useState<boolean>(false)
  const [roleOpen, setRoleOpen] = useState<boolean>(false)
  const [selectedStatus, setSelectedStatus] = useState<number>(0)
  const { id, branchId } = useParams<{ id: string; branchId: string }>()

  const ROLE_PERMISSION_OPTIONS: Option[] = [
    {
      label: '給与の閲覧',
      value:
        CompanyBranchRolePermissionBits[CompanyBranchRolePermission.SalaryView],
    },
    {
      label: '給与の編集',
      value:
        CompanyBranchRolePermissionBits[CompanyBranchRolePermission.SalaryEdit],
    },
    {
      label: '請求の閲覧',
      value:
        CompanyBranchRolePermissionBits[
          CompanyBranchRolePermission.BillingView
        ],
    },
    {
      label: '請求の編集',
      value:
        CompanyBranchRolePermissionBits[
          CompanyBranchRolePermission.BillingEdit
        ],
    },
    {
      label: '巡察の閲覧',
      value:
        CompanyBranchRolePermissionBits[
          CompanyBranchRolePermission.InspectionView
        ],
    },
    {
      label: '巡察の編集',
      value:
        CompanyBranchRolePermissionBits[
          CompanyBranchRolePermission.InspectionEdit
        ],
    },
  ]

  const getPermissionLabels = (permissions: number): string => {
    const permissionMap: { [key in CompanyBranchRolePermission]: string } = {
      [CompanyBranchRolePermission.SalaryView]: '給与の閲覧',
      [CompanyBranchRolePermission.SalaryEdit]: '給与の編集',
      [CompanyBranchRolePermission.BillingView]: '請求の閲覧',
      [CompanyBranchRolePermission.BillingEdit]: '請求の編集',
      [CompanyBranchRolePermission.InspectionView]: '巡察の閲覧',
      [CompanyBranchRolePermission.InspectionEdit]: '巡察の編集',
    }

    const permissionList = newCompanyBranchRolePermissions(permissions)
    return permissionList.map((p) => permissionMap[p]).join(', ')
  }
  const {
    control: userControl,
    handleSubmit: userHandleSubmit,
    formState: { errors },
    reset: userReset,
    setValue: userSetValue,
    watch: userWatch,
  } = useForm<UserForm>()

  const {
    control: branchControl,
    handleSubmit: branchHandleSubmit,
    reset: branchReset,
    watch,
    setValue,
  } = useForm<BranchForm>()

  const {
    control: roleControl,
    handleSubmit: roleHandleSubmit,
    formState: { errors: roleErrors },
    reset: roleReset,
  } = useForm<RoleForm>()

  const fetchData = () => {
    companyApi()
      .findBranchById(branchId!)
      .then((res) => {
        setCompanyBranch(res.data)
        branchReset(res.data)
      })
  }

  // 会社情報（全支店一覧）を取得
  const fetchCompany = () => {
    if (!id) return
    companyApi()
      .findById(id)
      .then((res) => {
        setCompany(res.data)
      })
  }

  useEffect(() => {
    fetchData()
    fetchCompany()
  }, [])

  const links = [
    {
      title: '警備会社一覧',
      to: '/company',
    },
    {
      title:
        !!companyBranch && !!companyBranch.company
          ? companyBranch.company?.name
          : '',
      to: `/company/${id}`,
    },
    {
      title: !!companyBranch ? companyBranch.branchName : '',
    },
  ]

  const showEditModal = async (user: User) => {
    // ユーザーのアクセス可能な支店一覧をAPIから取得
    let branchIds: string[] = []
    try {
      const res = await authApi().getUserCompanyBranches(user.id)
      branchIds = res.data.map((branch) => branch.id!) || []
    } catch (error) {
      console.error('Failed to fetch user company branches:', error)
    }

    const data: UserForm = {
      id: user.id,
      name: user.name,
      loginId: user.loginId,
      email: user.email,
      roleIds: user.roles?.map((role) => role.id) || [],
      status: user.status ?? STATUS.ACTIVE,
      branchIds,
    }
    userReset(data)
    setSelectedStatus(user.status ?? STATUS.ACTIVE)
    setOpen(true)
  }

  const showCreateModal = () => {
    userReset({
      loginId: '',
      name: '',
      email: '',
      roleIds: [],
      status: STATUS.ACTIVE,
      // 新規作成時は現在の支店のみをデフォルトで選択
      branchIds: branchId ? [branchId] : [],
    })
    setSelectedStatus(STATUS.ACTIVE)
    setOpen(true)
  }

  const handleCloseDialog = (): void => {
    userReset({})
    setOpen(false)
  }

  const showEditRoleModal = (role: any) => {
    // Convert bit flags to array of permission values
    const permissionBits = newCompanyBranchRolePermissions(role.permissions)
    const permissionValues = permissionBits.map(
      (p) => CompanyBranchRolePermissionBits[p],
    )

    const data: RoleForm = {
      id: role.id,
      name: role.name,
      permissions: permissionValues,
    }
    roleReset(data)
    setRoleOpen(true)
  }

  const showCreateRoleModal = () => {
    roleReset({})
    setRoleOpen(true)
  }

  const handleCloseRoleDialog = (): void => {
    roleReset({})
    setRoleOpen(false)
  }

  const onSubmit = (form: UserForm) => {
    // 削除ステータスに変更する場合の確認
    if (form.status === STATUS.DELETED) {
      const confirmText = window.prompt(
        'このアカウントを削除します。確認のため「削除」と入力してください。',
      )
      if (confirmText !== '削除') {
        alert('削除をキャンセルしました')
        return
      }
    }

    const data = {
      loginId: form.loginId.trim(),
      name: form.name.trim(),
      email: form.email.trim(),
      branchId: branchId!,
      roleIds: form.roleIds || [],
      status: form.status,
      branchIds: form.branchIds || [],
      companyBranchId: branchId!,
    }
    if (!!form.id) {
      if (confirm('更新しますか？')) {
        authApi()
          .updateUser(form.id, data)
          .then(() => {
            fetchData()
            handleCloseDialog()
          })
          .catch((error) => {
            alert(error?.data?.msg || '更新に失敗しました')
          })
      }
    } else {
      if (confirm('作成しますか？')) {
        authApi()
          .createUser(data)
          .then((res) => {
            const reader = new FileReader()
            reader.onload = (e: any) => {
              const tmpLink = document.createElement('a')
              const title = `${companyBranch?.company?.name}-${companyBranch?.branchName}-${data.name}.csv`
              tmpLink.download = title
              if (typeof e.target.result !== 'string') return
              tmpLink.href = e.target.result
              tmpLink.click()
            }

            const bom = new Uint8Array([0xef, 0xbb, 0xbf])
            const csvContent = new Blob([bom, res.data], {
              type: 'text/csv',
            })
            reader.readAsDataURL(csvContent)
            fetchData()
            handleCloseDialog()
            console.log('作成し、csvをダウンロードしました')
          })
          .catch((error) => {
            alert(error?.data?.msg || '作成に失敗しました')
          })
      } else {
        console.log('キャンセルしました')
      }
    }
  }

  const onBranchSubmit = (form: BranchForm) => {
    const data: CompanyBranch = {
      id: branchId!,
      ...form,
    }
    companyApi()
      .editBranch(branchId!, data)
      .then(() => {
        window.location.reload()
      })
  }

  const onRoleSubmit = (form: RoleForm) => {
    // Convert permission array to bit flag value
    const permissionBitValue = form.permissions.reduce(
      (acc, val) => acc | val,
      0,
    )

    const data = {
      name: form.name,
      permissions: permissionBitValue,
    }

    if (!!form.id) {
      if (confirm('権限を更新しますか？')) {
        companyApi()
          .updateRole(branchId!, form.id, data)
          .then(() => {
            fetchData()
            handleCloseRoleDialog()
          })
          .catch(() => {
            alert('権限の更新に失敗しました')
          })
      }
    } else {
      if (confirm('権限を作成しますか？')) {
        companyApi()
          .createRole(branchId!, data)
          .then(() => {
            fetchData()
            handleCloseRoleDialog()
          })
          .catch(() => {
            alert('権限の作成に失敗しました')
          })
      }
    }
  }

  return (
    <Box>
      <KanseiBreadcrumbs links={links} style={{ marginBottom: '15px' }} />
      <Grid
        container
        spacing={2}
        component="form"
        onSubmit={branchHandleSubmit(onBranchSubmit)}
      >
        <Grid item xs={2}>
          <KanseiTextField
            id="branchName"
            label="支店名"
            control={branchControl}
            required
          />
        </Grid>
        <Grid item xs={2}>
          <KanseiTextField
            id="postalCode"
            label="郵便番号"
            control={branchControl}
            required
          />
        </Grid>
        <Grid item xs={2}>
          <KanseiSimpleSelectBox
            label="都道府県 *"
            options={REGION_OPTIONS}
            value={String(watch('prefecture') || '')}
            onChange={(value) => setValue('prefecture', Number(value))}
            fullWidth
            required
          />
        </Grid>
        <Grid item xs={2}>
          <KanseiSimpleSelectBox
            label="契約タイプ *"
            options={CONTRACT_TYPE_OPTIONS}
            value={String(watch('contractType') ?? CONTRACT_TYPE.FULL)}
            onChange={(value) =>
              setValue('contractType', Number(value) as ContractType)
            }
            fullWidth
            required
          />
        </Grid>
        <Grid item xs={2}>
          <KanseiTextField
            id="address"
            label="住所"
            control={branchControl}
            required
          />
        </Grid>
        <Grid item xs={2}>
          <KanseiTextField
            id="phoneNumber"
            label="電話番号"
            control={branchControl}
            required
          />
        </Grid>
        <Grid item xs={2}>
          <KanseiTextField
            id="faxNumber"
            label="FAX番号"
            control={branchControl}
          />
        </Grid>
        <Grid item xs={12}>
          <KanseiTextField
            id="note"
            label="備考"
            control={branchControl}
            multiline
            rows={6}
          />
        </Grid>
        <Grid item xs={12}>
          <KanseiMultiSelectBox
            control={branchControl}
            name="plugins"
            options={PLUGIN_OPTIONS}
          />
        </Grid>
        <Grid item xs={12}>
          <KanseiButton buttonType="submit" variant="contained">
            更新
          </KanseiButton>
        </Grid>
      </Grid>
      <Divider sx={{ marginTop: '10px' }} />
      <Typography sx={{ margin: '10px 0' }}>アカウント情報</Typography>
      <KanseiButton onClick={showCreateModal}>新規作成</KanseiButton>
      <TableContainer component={Paper} sx={{ marginTop: '20px' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>編集</TableCell>
              <TableCell>名前</TableCell>
              <TableCell>ログインID</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>ロール</TableCell>
              <TableCell>ステータス</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {companyBranch?.users &&
              companyBranch?.users.map((user) => (
                <TableRow
                  key={user.id}
                  sx={{
                    ...getUserRowStyle(user.status),
                    '& td': getUserCellStyle(user.status),
                  }}
                >
                  <TableCell>
                    <IconButton
                      color="primary"
                      onClick={() => showEditModal(user)}
                    >
                      <Edit />
                    </IconButton>
                  </TableCell>
                  <TableCell>{user.name}</TableCell>
                  <TableCell>{user.loginId}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    {user.roles?.map((role) => role.name).join(', ') || ''}
                  </TableCell>
                  <TableCell>
                    {user.status === STATUS.DELETED ? (
                      <Typography color="error">削除</Typography>
                    ) : (
                      <Typography color="success">アクティブ</Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Divider sx={{ marginTop: '20px' }} />
      <Typography sx={{ margin: '10px 0' }}>権限管理</Typography>
      <KanseiButton onClick={showCreateRoleModal}>新規作成</KanseiButton>
      <TableContainer component={Paper} sx={{ marginTop: '20px' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>編集</TableCell>
              <TableCell>名前</TableCell>
              <TableCell>権限</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {companyBranch?.roles &&
              companyBranch?.roles.map((role) => (
                <TableRow key={role.id}>
                  <TableCell>
                    <IconButton
                      color="primary"
                      onClick={() => showEditRoleModal(role)}
                    >
                      <Edit />
                    </IconButton>
                  </TableCell>
                  <TableCell>{role.name}</TableCell>
                  <TableCell>{getPermissionLabels(role.permissions)}</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <KanseiDialog open={open} handleClose={handleCloseDialog} maxWidth="lg">
        <Box
          component="form"
          onSubmit={userHandleSubmit(onSubmit)}
          sx={{ width: '300px' }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <KanseiTextField
                id="name"
                label="アカウント名"
                control={userControl}
                errors={errors}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <KanseiTextField
                id="loginId"
                label="ログインID"
                control={userControl}
                errors={errors}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <KanseiTextField
                id="email"
                label="email"
                control={userControl}
                errors={errors}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <KanseiMultiSelectBox
                control={userControl}
                name="roleIds"
                options={
                  companyBranch?.roles?.map((role) => ({
                    label: role.name,
                    value: role.id,
                  })) || []
                }
              />
            </Grid>
            {/* 編集時のみ支店選択を表示（新規作成時は現在の支店に紐づく） */}
            {userWatch('id') && (
              <Grid item xs={12}>
                <Typography variant="body2">アクセス可能な支店</Typography>
                <KanseiMultiSelectBox
                  control={userControl}
                  name="branchIds"
                  options={
                    company?.companyBranches?.map((branch) => ({
                      label: branch.branchName,
                      value: branch.id!,
                    })) || []
                  }
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <Typography variant="body2">ステータス</Typography>
              <KanseiSimpleSelectBox
                options={STATUS_OPTIONS}
                value={selectedStatus.toString()}
                onChange={(value) => {
                  setSelectedStatus(Number(value))
                  userSetValue('status', Number(value))
                }}
                fullWidth
              />
            </Grid>
          </Grid>
          <br />
          <KanseiButton
            style={{ marginRight: '10px' }}
            onClick={handleCloseDialog}
          >
            キャンセル
          </KanseiButton>
          <KanseiButton buttonType="submit">
            {userWatch('id') ? '更新' : '作成'}
          </KanseiButton>
        </Box>
      </KanseiDialog>

      <KanseiDialog
        open={roleOpen}
        handleClose={handleCloseRoleDialog}
        maxWidth="lg"
      >
        <Box
          component="form"
          onSubmit={roleHandleSubmit(onRoleSubmit)}
          sx={{ width: '300px' }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <KanseiTextField
                id="name"
                label="権限名"
                control={roleControl}
                errors={roleErrors}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <KanseiMultiSelectBox
                control={roleControl}
                name="permissions"
                options={ROLE_PERMISSION_OPTIONS}
              />
            </Grid>
          </Grid>
          <br />
          <KanseiButton
            style={{ marginRight: '10px' }}
            onClick={handleCloseRoleDialog}
          >
            キャンセル
          </KanseiButton>
          <KanseiButton buttonType="submit">
            {roleControl._formValues?.id ? '更新' : '作成'}
          </KanseiButton>
        </Box>
      </KanseiDialog>

      {branchId && <SecurityComponent branchId={branchId} />}
    </Box>
  )
}

const getUserRowStyle = (status?: number): React.CSSProperties => ({
  backgroundColor: status === STATUS.DELETED ? '#f5f5f5' : 'inherit',
})

const getUserCellStyle = (status?: number): React.CSSProperties => ({
  color: status === STATUS.DELETED ? '#999' : 'inherit',
})
